import { environment } from 'src/environments/environment';
import { readStorage, removeStorage, writeStorage } from 'src/app/shared/utils/safe-storage';

/** Per-tab sessionStorage key holding the URL to return to after login. */
export const RETURN_URL_KEY = 'galactus.returnUrl';

/**
 * Per-tab sessionStorage key holding the id of the user who OWNS the captured URL, when it was
 * captured while a specific user was authenticated (a session-expiry deep-link). Absent for an
 * anonymous pre-login capture (the classic "you tried /app/x while logged out" flow), which is
 * restorable for whoever logs in. Kept separate from {@link RETURN_URL_KEY} so the URL value's
 * storage format is unchanged.
 */
export const RETURN_URL_OWNER_KEY = 'galactus.returnUrl.owner';

/**
 * Segment-safe test that a path is the `/app` route or a `/app/` sub-route — NOT a look-alike such as
 * `/apps` or `/application/...`. The single source of truth for the `/app` route boundary, shared by
 * {@link isRestorableAppUrl} (post-login restore) and `selectIsOnAppRoute` (per-login load gating) so
 * the two can't drift. Callers that may carry a query/hash should strip it before calling.
 */
export function isAppPath(path: string): boolean {
  return path === '/app' || path.startsWith('/app/');
}

/**
 * Strip the query string and fragment off a URL, leaving the route path {@link isAppPath} expects.
 *
 * {@code Router.url} carries them (`/app/orders/5?tab=2`), and while a sub-route still matches on the
 * `/app/` prefix, the bare root does not: `isAppPath('/app?tab=1')` is FALSE. Any caller passing a
 * live router URL must go through this — the single source of truth, shared with
 * `selectIsOnAppRoute`, so the two can't drift.
 */
export function routePathOf(url: string): string {
  // No `?? ''` fallback: String.prototype.split always returns at least one element (the whole string
  // when no separator matches), so index 0 is never undefined and a fallback would be unreachable code
  // that reads as if an empty URL were a real case to handle.
  return url.split(/[?#]/)[0];
}

/**
 * True only for internal in-app paths that are safe to navigate to after login.
 * Rejects empty values, /auth* (would loop back to login) and absolute/external
 * URLs (open-redirect guard). Matches only the `/app` route segment, so
 * look-alikes such as `/application/...` are rejected.
 */
export function isRestorableAppUrl(url: string | null | undefined): url is string {
  return (
    !!url &&
    isAppPath(url) &&
    !url.includes('://')
  );
}

/**
 * Store the attempted URL for post-login restore, only if it is restorable.
 *
 * Pass `ownerUid` when the URL is captured while a specific user is authenticated (a session-expiry
 * deep-link): it stamps the URL as that user's so a DIFFERENT user logging into the same tab can't
 * consume it (per-tab sessionStorage survives a logout). Omit it for an anonymous pre-login capture,
 * which stays restorable for whoever logs in. A capture always resets the owner so a stale stamp from
 * a prior capture can't linger.
 */
export function captureReturnUrl(url: string | null | undefined, ownerUid?: number | null): void {
  if (isRestorableAppUrl(url)) {
    // Guarded: storage refused here must cost the deep-link restore, never the login flow that
    // called us (an unguarded throw would abort the redirect to /auth/login entirely).
    if (!writeStorage('session', RETURN_URL_KEY, url)) {
      // A refused setItem writes NOTHING, so an earlier capture is still sitting in storage. Left
      // there it would be restored instead of this one -- a page the user has since navigated away
      // from, and if that leftover was anonymous, restorable by whoever signs into this tab. Fail
      // closed on both keys: no capture at all beats the wrong one.
      removeStorage('session', RETURN_URL_KEY);
      removeStorage('session', RETURN_URL_OWNER_KEY);
      return;
    }
    if (ownerUid !== undefined && ownerUid !== null) {
      // Fail closed: an owner-stamped capture whose stamp could not be written must not degrade
      // into an anonymous one, or a DIFFERENT user signing into this tab would consume the
      // previous user's deep-link -- exactly what the owner key exists to prevent.
      if (!writeStorage('session', RETURN_URL_OWNER_KEY, String(ownerUid))) {
        removeStorage('session', RETURN_URL_KEY);
        removeStorage('session', RETURN_URL_OWNER_KEY);
      }
    } else {
      removeStorage('session', RETURN_URL_OWNER_KEY);
    }
  }
}

/**
 * Read and clear the stored return URL, returning it when valid, otherwise the configured home page.
 * Always clears the keys so a stale value cannot linger.
 *
 * Pass `currentUid` (the just-authenticated user) so an owner-stamped URL is honoured ONLY for the
 * user who captured it: a different user signing into the same tab is sent to the home page instead of
 * the previous user's deep-link. An unstamped (anonymous) capture is restorable for any user.
 */
export function consumeReturnUrl(currentUid?: number | null): string {
  // A refused read yields null, i.e. "nothing captured" -- the caller falls back to the home page.
  const stored = readStorage('session', RETURN_URL_KEY);
  const owner = readStorage('session', RETURN_URL_OWNER_KEY);
  removeStorage('session', RETURN_URL_KEY);
  removeStorage('session', RETURN_URL_OWNER_KEY);
  if (!isRestorableAppUrl(stored)) {
    return environment.homePage;
  }
  // Owner-scoped: drop a deep-link captured by another user (only when we know who is consuming).
  if (owner !== null && currentUid !== undefined && currentUid !== null && owner !== String(currentUid)) {
    return environment.homePage;
  }
  return stored;
}
